import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import ItemCard from './ItemCard';
import { EposMenuCategory, EposMenuItem } from '../../types';

const Section = styled.section`
  /* Layout tokens live in styles/index.css - tighter on narrow screens. */
  padding: var(--eposSectionPadding);
`;

/** Category name with a bold rule on either side of it, like DividerWithText. */
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 28px;
`;

const SectionRule = styled.div`
  flex: 1;
  height: 2px;
  border-radius: 1px;
  background: var(--colorBlueGray300);
`;

const SectionTitle = styled.h2`
  flex: 0 0 auto;
  margin: 0;
  font-size: var(--eposSectionTitleSize);
  font-weight: 800;
  letter-spacing: -0.6px;
  color: var(--eposText);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(var(--eposGridMin), 1fr));
  gap: var(--eposGridGap);
`;

interface Props {
  category: EposMenuCategory;
  onOpenDetail: (item: EposMenuItem) => void;
  /** Ids of items that are out of stock right now - cards render them greyed out. */
  outOfStockIds: Set<string>;
}

/** One category block in the scrolling menu - also the scroll-spy target. */
const MenuSection: FC<Props> = ({ category, onOpenDetail, outOfStockIds }) => (
  <Section id={`epos-category-${category.id}`} data-category-id={category.id}>
    <SectionHeader>
      <SectionRule />
      <SectionTitle>{category.name}</SectionTitle>
      <SectionRule />
    </SectionHeader>
    <Grid>
      {category.items.map((item) => (
        <ItemCard
          key={item.id}
          item={item}
          onOpenDetail={onOpenDetail}
          unavailable={outOfStockIds.has(item.id)}
        />
      ))}
    </Grid>
  </Section>
);

export default memo(MenuSection);
